/**
 * Drizzle ORM + IndexedDB Implementation of IContractRepository
 */
import type { IContractRepository, ContractRecord } from './IContractRepository';
import { getIndexedDb, STORES } from '../db/db.web';

const SEED_CONTRACTS: ContractRecord[] = [
  {
    id: 'HD-1001-01',
    contractNumber: 'HD-78901234',
    customerId: 'KH-1001',
    customerName: 'Hoàng Cao Minh',
    productName: 'Manulife - Xanh Ước Mơ',
    sumAssured: '1.000.000.000',
    annualPremium: '28.500.000',
    effectiveDate: '15/01/2024',
    status: 'active',
    createdAt: '2024-01-15T10:00:00.000Z',
    updatedAt: '2024-01-15T10:00:00.000Z',
  },
  {
    id: 'HD-1003-01',
    contractNumber: 'HD-78905678',
    customerId: 'KH-1003',
    customerName: 'Nguyễn Văn A',
    productName: 'Manulife - Xanh Phú Quý',
    sumAssured: '2.000.000.000',
    annualPremium: '45.000.000',
    effectiveDate: '10/05/2023',
    status: 'active',
    createdAt: '2023-05-10T10:00:00.000Z',
    updatedAt: '2023-05-10T10:00:00.000Z',
  },
];

export class ContractRepository implements IContractRepository {
  private initialized = false;
  private memoryStore: ContractRecord[] = [...SEED_CONTRACTS];

  private async ensureInitialized(): Promise<void> {
    if (this.initialized) return;
    try {
      const db = await getIndexedDb();
      if (db) {
        const count = await db.count(STORES.CONTRACTS);
        if (count === 0) {
          const tx = db.transaction(STORES.CONTRACTS, 'readwrite');
          for (const item of SEED_CONTRACTS) {
            await tx.store.put(item);
          }
          await tx.done;
        }
        this.initialized = true;
      }
    } catch (err) {
      // IndexedDB not available
    }
  }

  async getByCustomerId(customerId: string): Promise<ContractRecord[]> {
    await this.ensureInitialized();
    try {
      const db = await getIndexedDb();
      const all: ContractRecord[] = await db.getAll(STORES.CONTRACTS);
      return all.filter((c) => c.customerId === customerId);
    } catch (err) {
      return this.memoryStore.filter((c) => c.customerId === customerId);
    }
  }

  async getAll(): Promise<ContractRecord[]> {
    await this.ensureInitialized();
    try {
      const db = await getIndexedDb();
      return await db.getAll(STORES.CONTRACTS);
    } catch (err) {
      return this.memoryStore;
    }
  }

  async getByContractNumber(contractNumber: string): Promise<ContractRecord | undefined> {
    await this.ensureInitialized();
    try {
      const db = await getIndexedDb();
      const all: ContractRecord[] = await db.getAll(STORES.CONTRACTS);
      return all.find((c) => c.contractNumber === contractNumber);
    } catch (err) {
      return this.memoryStore.find((c) => c.contractNumber === contractNumber);
    }
  }

  async save(data: Omit<ContractRecord, 'id' | 'createdAt' | 'updatedAt'>): Promise<ContractRecord> {
    await this.ensureInitialized();
    const nowIso = new Date().toISOString();

    const newRecord: ContractRecord = {
      ...data,
      id: `HD-${Date.now()}`,
      createdAt: nowIso,
      updatedAt: nowIso,
    };

    try {
      const db = await getIndexedDb();
      await db.put(STORES.CONTRACTS, newRecord);
    } catch (err) {
      this.memoryStore.push(newRecord);
    }

    return newRecord;
  }

  async update(id: string, partialData: Partial<ContractRecord>): Promise<ContractRecord | undefined> {
    await this.ensureInitialized();
    try {
      const db = await getIndexedDb();
      const existing: ContractRecord | undefined = await db.get(STORES.CONTRACTS, id);
      if (!existing) return undefined;

      const nowIso = new Date().toISOString();
      const updated: ContractRecord = {
        ...existing,
        ...partialData,
        updatedAt: nowIso,
      };

      await db.put(STORES.CONTRACTS, updated);
      return updated;
    } catch (err) {
      const index = this.memoryStore.findIndex((c) => c.id === id);
      if (index === -1) return undefined;
      const updated: ContractRecord = {
        ...this.memoryStore[index],
        ...partialData,
        updatedAt: new Date().toISOString(),
      };
      this.memoryStore[index] = updated;
      return updated;
    }
  }

  async delete(id: string): Promise<boolean> {
    await this.ensureInitialized();
    try {
      const db = await getIndexedDb();
      await db.delete(STORES.CONTRACTS, id);
      return true;
    } catch (err) {
      const index = this.memoryStore.findIndex((c) => c.id === id);
      if (index !== -1) {
        this.memoryStore.splice(index, 1);
        return true;
      }
      return false;
    }
  }
}

export const contractRepository = new ContractRepository();
